/* app/CommandPaletteContext.jsx — провайдер состояния командной палитры (S13, §2.1).
   Держит open/close/toggle + глобальный keydown-listener Ctrl/⌘K (везде, кроме фокуса
   в элементе с [data-no-palette] — напр. textarea Composer).

   Индекс поиска строится лениво при первом открытии: грузим БОЕВЫЕ реестры через lib/api
   (Сырьё/ТТК+п-ф/Меню/скиллы) один раз на пространство и кэшируем (смена пространства вкладки
   кэш сбрасывает — S10, kitchen-ux A2 круг 6), плюс текущий список чатов из
   ChatProvider (включая дочерние/spawned). При смене набора чатов индекс пересобирается
   из кэша сущностей (без повторных сетевых запросов).
   ХЕНДОФФ → frontend/src/contexts/CommandPaletteContext.jsx. */

import React from 'react';
import { useAuth } from './providers/AuthProvider.jsx';
import { useChat } from './providers/ChatProvider.jsx';
import { loaderOf } from './lazyScreen.js';
import { searchIndex } from '../lib/searchIndex.js';

  // Фасад реестров (lib/api/index.js) — по первому открытию: он тянет слой «Продукции» (адаптеры
  // ТТК, сырья, меню), а палитра живёт в оболочке — статический импорт вернул бы этот слой в
  // главный чанк (app/lazyScreens.js). Один промис на все загрузки (смена пространства, пока чанк
  // в пути, — вторая загрузка ждёт тот же чанк); сбой — следующее открытие пробует заново.
  const loadApi = loaderOf(() => import('../lib/api/index.js'));

  const { createContext, useContext, useState, useEffect, useCallback, useRef } = React;

  const Ctx = createContext(null);

  function CommandPaletteProvider({ children }) {
    const [open, setOpen] = useState(false);
    const chat = useChat ? useChat() : null;
    // активное пространство: индекс строится под его тарифные флаги (закрытая «Аналитика»
    // из палитры пропадает); смена/обновление пространства пересобирает индекс
    const auth = useAuth ? useAuth() : null;
    const activeWorkspace = auth ? auth.activeWorkspace : null;

    const openPalette = useCallback(() => setOpen(true), []);
    const closePalette = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((v) => !v), []);

    // глобальный Ctrl/⌘K
    useEffect(() => {
      const onKey = (e) => {
        const isK = (e.key === 'k' || e.key === 'K' || e.key === 'л' || e.key === 'Л');
        if (!isK || !(e.metaKey || e.ctrlKey)) return;
        // не перехватываем, если фокус в защищённом поле (Composer и т.п.)
        const ae = document.activeElement;
        if (ae && ae.closest && ae.closest('[data-no-palette]')) return;
        e.preventDefault();
        setOpen((v) => !v);
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, []);

    // боевые сущности (Сырьё/ТТК/Меню/скиллы) — грузим один раз на пространство, при первом
    // открытии. Каждый запрос best-effort: сбой одного реестра не рушит палитру (даёт []).
    // Кэш принадлежит пространству, для которого загружен (S10, kitchen-ux A2 круг 6): провайдер живёт
    // в оболочке и при смене пространства не перемонтируется — смена сбрасывает кэш, а поздний ответ
    // прежнего пространства (поколение загрузки устарело) в кэш нового не ложится. Иначе поиск
    // предлагал ТТК прежнего пространства, и выбор открывал её кадр в новом (404; у кадров кассы id
    // могут совпасть — тогда чужая позиция).
    const wsId = activeWorkspace ? activeWorkspace.id : null;
    const [entities, setEntities] = useState(null);
    const loadingRef = useRef(false);
    const genRef = useRef(0);
    const loadedForRef = useRef(wsId);
    useEffect(() => {
      if (loadedForRef.current === wsId) return;
      loadedForRef.current = wsId;
      genRef.current += 1;
      loadingRef.current = false;
      setEntities(null);
    }, [wsId]);
    useEffect(() => {
      if (!open || entities || loadingRef.current) return;
      loadingRef.current = true;
      const gen = genRef.current;
      loadApi().then((api) => {
        if (gen !== genRef.current) return null;                   // пространство сменилось, пока грузился фасад
        return Promise.all([
          api.listIngredients().catch(() => []),
          api.listTTKBriefs().catch(() => []),
          api.listMenuBriefs().catch(() => []),
          api.listSkills().catch(() => []),
        ]);
      }).then((loaded) => {
        if (!loaded || gen !== genRef.current) return;             // пространство сменилось, пока грузили
        const [ingredients, recipes, menus, skills] = loaded;
        setEntities({ ingredients, recipes, menus, skills });
      }).catch(() => { /* чанк фасада не загрузился — палитра ищет по чатам; повтор — при следующем открытии */ })
        .finally(() => { if (gen === genRef.current) loadingRef.current = false; });
    }, [open, entities, wsId]);

    // строим индекс при открытии; пересобираем при загрузке сущностей или смене набора чатов
    const [index, setIndex] = useState(null);
    const chats = chat ? chat.chats : null;
    useEffect(() => {
      if (!open) return;
      setIndex(searchIndex.buildSearchIndex({
        ingredients: (entities && entities.ingredients) || [],
        recipes: (entities && entities.recipes) || [],
        menus: (entities && entities.menus) || [],
        chats: chats || [],
        skills: (entities && entities.skills) || [],
        workspace: activeWorkspace,
      }));
    }, [open, entities, chats, activeWorkspace]);

    const value = {
      open, openPalette, closePalette, toggle,
      index: index || [],
      // последние чаты для пустого состояния
      recentChats: (chats || []).slice(0, 5),
    };
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  export const useCommandPalette = () => useContext(Ctx) || { open: false, openPalette: () => {}, closePalette: () => {}, toggle: () => {}, index: [], recentChats: [] };

export { CommandPaletteProvider };
